import { z } from "zod";
import { ConfidenceSchema, IdSchema, NonEmptyStringSchema, NonNegativeIntegerSchema, PositiveIntegerSchema, SourceRefSchema, LabeledDrawingEvidenceSchema, UnknownRecordSchema } from "./source.js";
export const HeadingMapEntrySchema = z
    .object({
    rawHeading: NonEmptyStringSchema,
    canonicalField: NonEmptyStringSchema,
    sourceColumn: NonEmptyStringSchema.optional(),
    confidence: ConfidenceSchema
})
    .strict();
export const TakeoffWarningSchema = z
    .object({
    code: NonEmptyStringSchema,
    message: NonEmptyStringSchema,
    severity: z.enum(["info", "warning", "blocker"]),
    sourceRefs: z.array(SourceRefSchema)
})
    .strict();
export const ApartmentTakeoffRowSchema = z
    .object({
    apartmentTypeId: IdSchema,
    rawLabel: NonEmptyStringSchema,
    quantity: PositiveIntegerSchema,
    beds: NonNegativeIntegerSchema.optional(),
    fcuCount: NonNegativeIntegerSchema.optional(),
    towelRailCount: NonNegativeIntegerSchema.optional(),
    wufhZoneCount: NonNegativeIntegerSchema.optional(),
    wetActuatorCount: NonNegativeIntegerSchema.optional(),
    eufhZoneCount: NonNegativeIntegerSchema.optional(),
    standaloneHeatOnlyZoneCount: NonNegativeIntegerSchema.optional().default(0),
    thermostatUiPointCount: NonNegativeIntegerSchema.optional(),
    centralControlScreenCount: NonNegativeIntegerSchema.optional(),
    hiuCount: NonNegativeIntegerSchema.optional(),
    ciuCount: NonNegativeIntegerSchema.optional(),
    althermaCount: NonNegativeIntegerSchema.optional(),
    sourceRefs: z.array(SourceRefSchema).min(1),
    rawFields: UnknownRecordSchema,
    normalizedFrom: z.array(HeadingMapEntrySchema),
    confidence: ConfidenceSchema,
    warnings: z.array(TakeoffWarningSchema)
})
    .strict();
export const ENGINE_SAFE_TAKEOFF_FIELD_NAMES = [
    "type_code",
    "apartment_type_no",
    "core",
    "occupancy",
    "bedrooms",
    "wet_rooms_total",
    "fcu_zones",
    "fcu_locations",
    "thermostat_positions",
    "thermostat_locations",
    "central_screen_position_drawn",
    "wet_ufh_zones",
    "corridor_ufh_zones",
    "bathroom_eufh_zones",
    "total_ufh_zones",
    "hiu_qty",
    "mvhr_qty",
    "electric_towel_rails",
    "energy_meters",
    "comfort_cooling",
    "fcu_band",
    "source_drawings",
    "evidence_notes",
    "assumptions",
    "flags_questions",
    "confidence"
];
export const EngineSafeTakeoffFieldNameSchema = z.enum([
    ...ENGINE_SAFE_TAKEOFF_FIELD_NAMES
]);
export const EngineSafeTakeoffCountsSchema = z
    .object({
    quantity: PositiveIntegerSchema,
    beds: NonNegativeIntegerSchema.optional(),
    fcuCount: NonNegativeIntegerSchema.optional(),
    towelRailCount: NonNegativeIntegerSchema.optional(),
    wufhZoneCount: NonNegativeIntegerSchema.optional(),
    wetActuatorCount: NonNegativeIntegerSchema.optional(),
    eufhZoneCount: NonNegativeIntegerSchema.optional(),
    standaloneHeatOnlyZoneCount: NonNegativeIntegerSchema.optional(),
    thermostatUiPointCount: NonNegativeIntegerSchema.optional(),
    centralControlScreenCount: NonNegativeIntegerSchema.optional(),
    hiuCount: NonNegativeIntegerSchema.optional(),
    ciuCount: NonNegativeIntegerSchema.optional(),
    althermaCount: NonNegativeIntegerSchema.optional()
})
    .strict();
const EngineSafeTakeoffConfidenceBandSchema = z.enum(["low", "medium", "high"]);
const EngineSafeTakeoffPresenceSchema = z.enum(["present", "absent", "unknown"]);
/**
 * This is deliberately a positive field list. A future drawing-reader field is
 * invisible to the engine until its name and non-text value type are added
 * here. Product decisions still have no representable value in this object;
 * separately labeled drawing evidence is carried in `drawingEvidence` below.
 */
export const EngineSafeTakeoffFieldValuesSchema = z
    .object({
    apartment_type_no: NonNegativeIntegerSchema.optional(),
    bedrooms: NonNegativeIntegerSchema.optional(),
    wet_rooms_total: NonNegativeIntegerSchema.optional(),
    fcu_zones: NonNegativeIntegerSchema.optional(),
    thermostat_positions: NonNegativeIntegerSchema.optional(),
    central_screen_position_drawn: z
        .union([NonNegativeIntegerSchema, z.boolean()])
        .optional(),
    wet_ufh_zones: NonNegativeIntegerSchema.optional(),
    corridor_ufh_zones: NonNegativeIntegerSchema.optional(),
    bathroom_eufh_zones: NonNegativeIntegerSchema.optional(),
    total_ufh_zones: NonNegativeIntegerSchema.optional(),
    hiu_qty: NonNegativeIntegerSchema.optional(),
    mvhr_qty: NonNegativeIntegerSchema.optional(),
    electric_towel_rails: NonNegativeIntegerSchema.optional(),
    energy_meters: NonNegativeIntegerSchema.optional(),
    comfort_cooling: z.union([z.boolean(), EngineSafeTakeoffPresenceSchema]).optional(),
    confidence: z
        .union([ConfidenceSchema, EngineSafeTakeoffConfidenceBandSchema])
        .optional()
})
    .strict();
export const EngineSafeTakeoffLocatorSchema = z
    .object({
    field: EngineSafeTakeoffFieldNameSchema,
    renderObjectId: IdSchema,
    tileId: IdSchema.optional(),
    page: PositiveIntegerSchema
})
    .strict();
export const EngineSafeTakeoffFieldStatusSchema = z.enum([
    "accepted",
    "revised",
    "blocked",
    "needs_review",
    "conflict",
    "omitted"
]);
export const EngineSafeTakeoffStatusFlagSchema = z.enum([
    "conflict",
    "omission",
    "needs_review"
]);
const EngineSafeTakeoffStageStatusSchema = z.enum([
    "queued",
    "running",
    "succeeded",
    "failed",
    "blocked",
    "waived",
    "cancelled",
    "superseded",
    "deleted"
]);
export const EngineSafeTakeoffStatusSchema = z
    .object({
    stageStatus: EngineSafeTakeoffStageStatusSchema.optional(),
    flags: z.array(EngineSafeTakeoffStatusFlagSchema),
    fields: z.array(z
        .object({
        field: EngineSafeTakeoffFieldNameSchema,
        status: EngineSafeTakeoffFieldStatusSchema
    })
        .strict())
})
    .strict();
/**
 * The sole drawing-takeoff shape permitted across a specification-engine or
 * deciding-agent boundary. It intentionally cannot carry raw labels, reasons,
 * summaries, notes, outcomes, issue strings, reviewer text or arbitrary raw
 * fields. Drawing text may cross only through the strict, explicitly labeled
 * `drawingEvidence` shape; reviewer-facing artifacts retain the rest.
 */
export const EngineSafeTakeoffProjectionSchema = z
    .object({
    apartmentTypeId: IdSchema,
    counts: EngineSafeTakeoffCountsSchema,
    fieldValues: EngineSafeTakeoffFieldValuesSchema,
    locators: z.array(EngineSafeTakeoffLocatorSchema),
    status: EngineSafeTakeoffStatusSchema,
    drawingEvidence: z.array(LabeledDrawingEvidenceSchema).optional()
})
    .strict();
export const ENGINE_SAFE_TAKEOFF_RAW_FIELDS_KEY = "engineSafeTakeoff";
export const DRAWING_EVIDENCE_KEY = "drawingEvidence";
/** Drawing-born here means provenance emitted by the takeoff artifact itself. */
export function isDrawingTakeoffArtifactSourceRef(sourceRef) {
    return sourceRef.sourceKind === "drawing" && sourceRef.authorityClass === "drawing";
}
/**
 * Projects either a takeoff artifact, an ApartmentTakeoffRow, or an already
 * projected value through the same allowlist. The unknown input is deliberate:
 * this is the trust-boundary parser, and no caller-supplied static type is
 * allowed to widen the output.
 */
export function projectTakeoffForEngine(value) {
    const directProjection = EngineSafeTakeoffProjectionSchema.safeParse(value);
    if (directProjection.success) {
        return directProjection.data;
    }
    const input = takeoffRecord(value);
    const rawFields = takeoffRecord(input.rawFields);
    const embeddedProjection = EngineSafeTakeoffProjectionSchema.safeParse(input[ENGINE_SAFE_TAKEOFF_RAW_FIELDS_KEY] ??
        rawFields[ENGINE_SAFE_TAKEOFF_RAW_FIELDS_KEY]);
    const embedded = embeddedProjection.success ? embeddedProjection.data : undefined;
    const apartmentTypeId = IdSchema.parse(input.apartmentTypeId ?? embedded?.apartmentTypeId);
    const artifactFields = takeoffRecord(input.fields);
    const legacyTakeoffFields = takeoffRecord(rawFields.tenderTakeoff);
    const sourceFieldValue = (field) => {
        const artifactField = takeoffRecord(artifactFields[field]);
        if ("value" in artifactField) {
            return artifactField.value;
        }
        const legacyField = legacyTakeoffFields[field];
        const legacyFieldRecord = takeoffRecord(legacyField);
        if ("value" in legacyFieldRecord) {
            return legacyFieldRecord.value;
        }
        return legacyField ?? takeoffRecord(embedded?.fieldValues)[field];
    };
    const embeddedCounts = embedded?.counts;
    const count = (canonicalField, artifactField) => takeoffNonNegativeInteger(input[canonicalField]) ??
        takeoffNonNegativeInteger(embeddedCounts?.[canonicalField]) ??
        (artifactField
            ? takeoffNonNegativeInteger(sourceFieldValue(artifactField))
            : undefined);
    const quantity = takeoffPositiveInteger(input.quantity) ??
        takeoffPositiveInteger(embeddedCounts?.quantity);
    if (quantity === undefined) {
        throw new Error("Engine-safe takeoff projection requires a positive quantity.");
    }
    const wetUfh = takeoffNonNegativeInteger(sourceFieldValue("wet_ufh_zones"));
    const corridorUfh = takeoffNonNegativeInteger(sourceFieldValue("corridor_ufh_zones"));
    const centralScreenValue = sourceFieldValue("central_screen_position_drawn");
    const counts = {
        quantity,
        ...optionalTakeoffCount("beds", count("beds", "bedrooms")),
        ...optionalTakeoffCount("fcuCount", count("fcuCount", "fcu_zones")),
        ...optionalTakeoffCount("towelRailCount", count("towelRailCount", "electric_towel_rails")),
        ...optionalTakeoffCount("wufhZoneCount", count("wufhZoneCount") ??
            (wetUfh !== undefined && corridorUfh !== undefined
                ? wetUfh + corridorUfh
                : undefined)),
        ...optionalTakeoffCount("wetActuatorCount", count("wetActuatorCount")),
        ...optionalTakeoffCount("eufhZoneCount", count("eufhZoneCount", "bathroom_eufh_zones")),
        ...optionalTakeoffCount("standaloneHeatOnlyZoneCount", count("standaloneHeatOnlyZoneCount")),
        ...optionalTakeoffCount("thermostatUiPointCount", count("thermostatUiPointCount", "thermostat_positions")),
        ...optionalTakeoffCount("centralControlScreenCount", count("centralControlScreenCount") ??
            (typeof centralScreenValue === "boolean"
                ? centralScreenValue
                    ? 1
                    : 0
                : takeoffNonNegativeInteger(centralScreenValue))),
        ...optionalTakeoffCount("hiuCount", count("hiuCount", "hiu_qty")),
        ...optionalTakeoffCount("ciuCount", count("ciuCount")),
        ...optionalTakeoffCount("althermaCount", count("althermaCount"))
    };
    const fieldValues = engineSafeFieldValues({
        input,
        sourceFieldValue,
        embedded: embedded?.fieldValues
    });
    const locators = engineSafeTakeoffLocators({
        artifactFields,
        embedded: embedded?.locators ?? []
    });
    const status = engineSafeTakeoffStatus({
        input,
        artifactFields,
        embedded: embedded?.status
    });
    const drawingEvidence = engineSafeDrawingEvidence({
        input,
        rawFields,
        embedded: embedded?.drawingEvidence
    });
    return EngineSafeTakeoffProjectionSchema.parse({
        apartmentTypeId,
        counts,
        fieldValues,
        locators,
        status,
        ...(drawingEvidence.length > 0 ? { drawingEvidence } : {})
    });
}
function engineSafeDrawingEvidence(input) {
    const candidates = [
        input.input[DRAWING_EVIDENCE_KEY],
        input.rawFields[DRAWING_EVIDENCE_KEY],
        input.embedded
    ];
    const evidence = [];
    const seen = new Set();
    for (const candidate of candidates) {
        if (candidate === undefined) {
            continue;
        }
        const parsed = z.array(LabeledDrawingEvidenceSchema).safeParse(candidate);
        if (!parsed.success) {
            continue;
        }
        for (const item of parsed.data) {
            const key = JSON.stringify(item);
            if (seen.has(key)) {
                continue;
            }
            seen.add(key);
            evidence.push(item);
        }
    }
    return evidence;
}
function engineSafeFieldValues(input) {
    const values = { ...(input.embedded ?? {}) };
    const integerFields = [
        "apartment_type_no",
        "bedrooms",
        "wet_rooms_total",
        "fcu_zones",
        "thermostat_positions",
        "wet_ufh_zones",
        "corridor_ufh_zones",
        "bathroom_eufh_zones",
        "total_ufh_zones",
        "hiu_qty",
        "mvhr_qty",
        "electric_towel_rails",
        "energy_meters"
    ];
    for (const field of integerFields) {
        const numeric = takeoffNonNegativeInteger(input.sourceFieldValue(field));
        if (numeric !== undefined) {
            values[field] = numeric;
        }
    }
    const centralScreen = input.sourceFieldValue("central_screen_position_drawn");
    if (typeof centralScreen === "boolean") {
        values.central_screen_position_drawn = centralScreen;
    }
    else {
        const numeric = takeoffNonNegativeInteger(centralScreen);
        if (numeric !== undefined) {
            values.central_screen_position_drawn = numeric;
        }
    }
    const comfortCooling = input.sourceFieldValue("comfort_cooling");
    const comfortCoolingEnum = takeoffPresenceValue(comfortCooling);
    if (typeof comfortCooling === "boolean") {
        values.comfort_cooling = comfortCooling;
    }
    else if (comfortCoolingEnum !== undefined) {
        values.comfort_cooling = comfortCoolingEnum;
    }
    const confidence = input.sourceFieldValue("confidence") ?? input.input.confidence;
    const numericConfidence = ConfidenceSchema.safeParse(confidence);
    if (numericConfidence.success) {
        values.confidence = numericConfidence.data;
    }
    else if (typeof confidence === "string") {
        const band = EngineSafeTakeoffConfidenceBandSchema.safeParse(confidence.trim().toLowerCase());
        if (band.success) {
            values.confidence = band.data;
        }
    }
    return EngineSafeTakeoffFieldValuesSchema.parse(values);
}
function engineSafeTakeoffLocators(input) {
    const locators = [...input.embedded];
    for (const field of ENGINE_SAFE_TAKEOFF_FIELD_NAMES) {
        const provenance = takeoffRecord(takeoffRecord(input.artifactFields[field]).provenance);
        const renderObjectId = IdSchema.safeParse(provenance.renderObjectId);
        const tileId = IdSchema.safeParse(provenance.tileId);
        const page = PositiveIntegerSchema.safeParse(provenance.page);
        if (!renderObjectId.success || !page.success) {
            continue;
        }
        locators.push({
            field,
            renderObjectId: renderObjectId.data,
            ...(tileId.success ? { tileId: tileId.data } : {}),
            page: page.data
        });
    }
    const byKey = new Map();
    for (const locator of locators) {
        const parsed = EngineSafeTakeoffLocatorSchema.parse(locator);
        byKey.set(`${parsed.field}\0${parsed.renderObjectId}\0${parsed.tileId ?? ""}\0${parsed.page}`, parsed);
    }
    const fieldOrder = new Map(ENGINE_SAFE_TAKEOFF_FIELD_NAMES.map((field, index) => [field, index]));
    return [...byKey.values()].sort((left, right) => (fieldOrder.get(left.field) ?? 0) - (fieldOrder.get(right.field) ?? 0) ||
        left.renderObjectId.localeCompare(right.renderObjectId) ||
        (left.tileId ?? "").localeCompare(right.tileId ?? "") ||
        left.page - right.page);
}
function engineSafeTakeoffStatus(input) {
    const statusByField = new Map((input.embedded?.fields ?? []).map((entry) => [entry.field, entry.status]));
    const flags = new Set(input.embedded?.flags ?? []);
    const reconciliation = takeoffRecord(input.input.reconciliation);
    const decisions = Array.isArray(reconciliation.fieldDecisions)
        ? reconciliation.fieldDecisions
        : [];
    for (const decision of decisions) {
        const record = takeoffRecord(decision);
        const field = EngineSafeTakeoffFieldNameSchema.safeParse(record.field);
        const status = EngineSafeTakeoffFieldStatusSchema.safeParse(record.decision);
        if (field.success && status.success) {
            statusByField.set(field.data, status.data);
        }
    }
    const disagreements = Array.isArray(reconciliation.disagreements)
        ? reconciliation.disagreements
        : [];
    for (const disagreement of disagreements) {
        const field = EngineSafeTakeoffFieldNameSchema.safeParse(takeoffRecord(disagreement).field);
        if (field.success) {
            statusByField.set(field.data, "conflict");
            flags.add("conflict");
            flags.add("needs_review");
        }
    }
    for (const field of ENGINE_SAFE_TAKEOFF_FIELD_NAMES) {
        const value = takeoffRecord(takeoffRecord(input.artifactFields[field]).value);
        if (value.conflict !== true) {
            continue;
        }
        const status = Array.isArray(value.candidates) && value.candidates.length === 0
            ? "omitted"
            : "conflict";
        statusByField.set(field, status);
        flags.add(status === "omitted" ? "omission" : "conflict");
        flags.add("needs_review");
    }
    const reviewerFlags = Array.isArray(reconciliation.reviewerFlags)
        ? reconciliation.reviewerFlags
        : [];
    if (reviewerFlags.includes("no_values_read")) {
        flags.add("omission");
        flags.add("needs_review");
    }
    if (reviewerFlags.includes("needs_user_decision")) {
        flags.add("needs_review");
    }
    for (const status of statusByField.values()) {
        if (status === "blocked" || status === "needs_review") {
            flags.add("needs_review");
        }
    }
    const stageStatus = EngineSafeTakeoffStageStatusSchema.safeParse(input.input.stageStatus ?? input.embedded?.stageStatus);
    const fieldOrder = new Map(ENGINE_SAFE_TAKEOFF_FIELD_NAMES.map((field, index) => [field, index]));
    const flagOrder = ["conflict", "omission", "needs_review"];
    return EngineSafeTakeoffStatusSchema.parse({
        ...(stageStatus.success ? { stageStatus: stageStatus.data } : {}),
        flags: [...flags].sort((left, right) => flagOrder.indexOf(left) - flagOrder.indexOf(right)),
        fields: [...statusByField.entries()]
            .map(([field, status]) => ({ field, status }))
            .sort((left, right) => (fieldOrder.get(left.field) ?? 0) - (fieldOrder.get(right.field) ?? 0))
    });
}
function takeoffPresenceValue(value) {
    if (typeof value !== "string") {
        return undefined;
    }
    const normalized = value.trim().toLowerCase();
    if (normalized === "yes") {
        return "present";
    }
    if (normalized === "no") {
        return "absent";
    }
    const parsed = EngineSafeTakeoffPresenceSchema.safeParse(normalized);
    return parsed.success ? parsed.data : undefined;
}
function optionalTakeoffCount(key, value) {
    return value === undefined
        ? {}
        : { [key]: value };
}
function takeoffRecord(value) {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value
        : {};
}
function takeoffNonNegativeInteger(value) {
    const parsed = NonNegativeIntegerSchema.safeParse(value);
    return parsed.success ? parsed.data : undefined;
}
function takeoffPositiveInteger(value) {
    const parsed = PositiveIntegerSchema.safeParse(value);
    return parsed.success ? parsed.data : undefined;
}
export const ControlZoneSchema = z
    .object({
    id: IdSchema,
    apartmentTypeId: IdSchema,
    label: NonEmptyStringSchema,
    roomName: NonEmptyStringSchema.optional(),
    zoneKind: z.enum([
        "fcu",
        "wufh",
        "eufh",
        "towel_rail",
        "heat_only",
        "cooling",
        "metering",
        "other"
    ]),
    requiresTemperatureInput: z.boolean(),
    sourceRefs: z.array(SourceRefSchema).min(1),
    confidence: ConfidenceSchema
})
    .strict();
export const ApartmentTypeSchema = z
    .object({
    id: IdSchema,
    projectId: IdSchema,
    label: NonEmptyStringSchema,
    quantity: PositiveIntegerSchema,
    beds: NonNegativeIntegerSchema.optional(),
    zones: z.array(ControlZoneSchema),
    sourceTakeoffRowIds: z.array(IdSchema),
    sourceRefs: z.array(SourceRefSchema).min(1),
    rawFields: UnknownRecordSchema
})
    .strict();
//# sourceMappingURL=takeoff.js.map